import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchEntityDocuments } from '../../lib/exploreApi';
import type { ExploreSearchResult, EntityDocumentsResponse } from '../../lib/exploreApi';
import EntityContextMenu from '../../components/EntityContextMenu';
import type { EntityDescriptor } from '../../components/EntityContextMenu';
import { actorDetailPath, familyDetailPath, techniqueDetailPath, iocDetailPath, countryDetailPath } from '../../lib/paths';
import { countryFlag, countryLabel } from '../../lib/countryFlag';
import { truncateHash } from '../../lib/iocFormat';

// Categories that do NOT have a `/ioc/` detail page (they go to /graph or just expand).
const NON_DETAIL_CATEGORIES = new Set<string>([]);

interface ResultRowProps {
  item: ExploreSearchResult;
}

const IOC_CATEGORIES = new Set(['hash', 'ip', 'domain', 'url', 'email', 'filepath']);

const BADGE_CLASS: Record<string, string> = {
  ioc: 'explore-badge-ioc',
  cve: 'explore-badge-cve',
  technique: 'explore-badge-technique',
  actor: 'explore-badge-actor',
  family: 'explore-badge-family',
};

function extraDetails(item: ExploreSearchResult): React.ReactNode[] {
  const chips: React.ReactNode[] = [];
  if (!item.extra) return chips;

  if (item.category === 'cve') {
    if (item.extra.cvss_severity) {
      const sev = String(item.extra.cvss_severity).toLowerCase();
      chips.push(
        <span key="sev" className={`explore-severity-badge explore-severity-${sev}`}>
          {item.extra.cvss_score != null ? `${item.extra.cvss_score} ` : ''}
          {String(item.extra.cvss_severity)}
        </span>
      );
    }
    if (item.extra.in_cisa_kev) {
      chips.push(
        <span key="kev" className="explore-kev-badge">KEV</span>
      );
    }
  }

  if (item.category === 'technique') {
    if (item.extra.attack_id) {
      chips.push(
        <span key="aid" className="explore-attack-id">{String(item.extra.attack_id)}</span>
      );
    }
    if (item.extra.tactics) {
      const tactics = String(item.extra.tactics);
      tactics.split(',').filter(Boolean).forEach((t, i) => {
        chips.push(
          <span key={`tactic-${i}`} className="explore-tactic-pill">{t.trim()}</span>
        );
      });
    }
  }

  return chips;
}

export default function ResultRow({ item }: ResultRowProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{ descriptor: EntityDescriptor; position: { x: number; y: number } } | null>(null);

  const { data: docsData, isLoading: docsLoading } = useQuery<EntityDocumentsResponse>({
    queryKey: ['explore-entity-docs', item.value, item.category],
    queryFn: () => fetchEntityDocuments({
      value: item.category === 'technique' && item.extra?.attack_id
        ? String(item.extra.attack_id)
        : item.value,
      category: item.category,
    }),
    enabled: expanded,
  });

  const isClickable = !NON_DETAIL_CATEGORIES.has(item.category);
  const handleValueClick = () => {
    if (item.category === 'country') {
      navigate(countryDetailPath(item.value));
      return;
    }
    if (item.category === 'actor') {
      navigate(actorDetailPath(item.value));
      return;
    }
    if (item.category === 'family') {
      navigate(familyDetailPath(item.value));
      return;
    }
    if (item.category === 'technique') {
      const aid = item.extra?.attack_id ? String(item.extra.attack_id) : item.value;
      navigate(techniqueDetailPath(aid));
      return;
    }
    if (isClickable) {
      navigate(iocDetailPath(item.value, item.category));
    } else {
      setExpanded(!expanded);
    }
  };

  return (
    <div
      className={`explore-result-row${expanded ? ' explore-result-row--expanded' : ''}`}
      onContextMenu={(e) => {
        e.preventDefault();
        setCtxMenu({
          descriptor: {
            value: item.value,
            category: item.category,
            subType: item.sub_type ?? undefined,
            attackId: item.extra?.attack_id ? String(item.extra.attack_id) : undefined,
          },
          position: { x: e.clientX, y: e.clientY },
        });
      }}
    >
      <div className="explore-result-row-main">
        <span className={`explore-result-badge ${BADGE_CLASS[item.category] || ''}`}>
          {item.category === 'technique' ? 'ATT&CK' : item.category.toUpperCase()}
        </span>

        <button
          className={`explore-result-value${isClickable ? ' explore-result-value--link' : ''}`}
          onClick={handleValueClick}
          title={item.value}
        >
          {item.category === 'country' && countryFlag(item.value) && (
            <span aria-hidden="true" style={{ marginRight: 6 }}>{countryFlag(item.value)}</span>
          )}
          {item.category === 'hash' ? truncateHash(item.value) : item.value}
        </button>

        {item.sub_type && (
          item.category === 'actor' ? (
            <span className="explore-country-badge" title={countryLabel(item.sub_type) ?? item.sub_type}>
              {countryFlag(item.sub_type) && <span aria-hidden="true" style={{ marginRight: 4 }}>{countryFlag(item.sub_type)}</span>}
              {item.sub_type}
            </span>
          ) : (
            <span className="explore-result-subtype">{item.sub_type}</span>
          )
        )}

        {extraDetails(item)}

        {!IOC_CATEGORIES.has(item.category) && (
          <span className="explore-result-count" title={`Referenced in ${item.doc_count} documents`}>
            {item.doc_count}
          </span>
        )}

        <button
          className={`explore-result-expand${expanded ? ' explore-result-expand--open' : ''}`}
          onClick={() => setExpanded(!expanded)}
          title={expanded ? 'Collapse' : 'Show documents'}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4,5 7,8 10,5" />
          </svg>
        </button>
      </div>

      {expanded && (
        <div className="explore-result-docs">
          {docsLoading ? (
            <div className="explore-result-docs-loading">Loading documents...</div>
          ) : docsData && docsData.documents.length > 0 ? (
            <table className="explore-result-docs-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Date</th>
                  <th>Organization</th>
                </tr>
              </thead>
              <tbody>
                {docsData.documents.map((doc) => (
                  <tr key={doc.cite_key}>
                    <td>
                      <Link to={`/sources/${encodeURIComponent(doc.cite_key)}`} className="explore-doc-link">
                        {doc.title || doc.cite_key}
                      </Link>
                    </td>
                    <td className="explore-doc-date">{doc.date || '-'}</td>
                    <td className="explore-doc-org">{doc.organization || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="explore-result-docs-empty">No documents found</div>
          )}
          {docsData && docsData.total > docsData.documents.length && (
            <div className="explore-result-docs-more">
              Showing {docsData.documents.length} of {docsData.total} documents
            </div>
          )}
        </div>
      )}

      {ctxMenu && (
        <EntityContextMenu
          descriptor={ctxMenu.descriptor}
          position={ctxMenu.position}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}
